/*
 * Reading a Jobber or BookingKoala client export (draft 14, FEATURE-MAP 15), ported from the WordPress Steward's
 * import.php and 20-import.js. The header row is matched by name, exact names first, then looser ones, so a Jobber or
 * BookingKoala export (or a plain sheet) lines up with no renaming. Each row becomes a client's fields plus the problems
 * that stop it, and nothing is saved here.
 *
 * Kept from the other system: the name and company, every phone and email, the service address, tags, notes, the lead
 * source, the day they became a client, the services done (kinds, first and last job, how many), and what they spent
 * before the CRM ("Paid before the CRM", with the job numbers). An archived client comes in turned off.
 */
import { normalizePhone } from "./api.zod.js";

/** A phone that reads: +1 and 10 digits, or another country with 8 to 15 digits. Else null. */
function readPhone(raw: string): string | null {
  const n = normalizePhone(raw);
  return /^\+1\d{10}$/.test(n) || /^\+[2-9]\d{7,14}$/.test(n) ? n : null;
}

/** Splits CSV (or tab separated) text into rows of cells. Quotes and doubled quotes inside them are read as in Excel. */
export function csvRows(text: string): string[][] {
  const src = text.replace(/^\ufeff/, "");
  const delim = src.split(/\r?\n/, 1)[0]!.includes("\t") ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delim) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((x) => x.trim()));
}

/** Each field, the column names it takes (exact first, then any header holding the words), as in the old Steward. */
export const IMPORT_HINTS: readonly (readonly [string, readonly string[]])[] = [
  ["jobberId", ["j id"]],
  ["importId", ["client id", "customer id"]],
  ["company", ["company name", "company", "business name", "business", "organization"]],
  ["first", ["first name", "firstname", "first"]],
  ["last", ["last name", "lastname", "last", "surname"]],
  ["name", ["display name", "customer name", "client name", "full name", "name", "client", "customer"]],
  ["email", ["email address", "e mails", "e mail", "emails", "email"]],
  ["email2", ["second email", "other email", "alternate email", "email 2"]],
  ["phoneText", ["text message enabled phone"]],
  ["phoneMobile", ["mobile phone", "mobile", "cell phone"]],
  ["phoneMain", ["main phone"]],
  ["phoneHome", ["home phone"]],
  ["phoneWork", ["work phone"]],
  ["phoneOther", ["other phone", "second phone", "alternate phone", "phone 2"]],
  ["phoneNumber", ["phone number"]],
  ["phone", ["phone"]],
  ["cell", ["cell"]],
  [
    "address",
    [
      "service street 1",
      "property street 1",
      "service address",
      "property address",
      "street address",
      "address 1",
      "address line 1",
      "address",
      "street 1",
      "street",
    ],
  ],
  ["address2", ["service street 2", "property street 2", "address 2", "address line 2", "street 2", "apt", "unit", "suite"]],
  ["city", ["service city", "property city", "city", "town"]],
  ["state", ["service state", "property state", "state", "province"]],
  ["zip", ["service zip", "property zip", "zip code", "zipcode", "postal code", "zip", "postal"]],
  ["createdAt", ["created date", "client since", "customer since", "date added"]],
  ["servicesDone", ["exterior services", "services done", "services had", "service history", "services"]],
  ["jobsCount", ["exterior jobs", "jobs done", "number of jobs", "job count"]],
  ["svcFirst", ["first exterior job", "first job", "first service"]],
  ["svcLast", ["last exterior job", "last job", "last service"]],
  ["spend", ["exterior spend", "total spend", "lifetime spend", "lifetime value", "spend"]],
  ["jobNumbers", ["jobber job numbers", "job numbers"]],
  ["textOk", ["text ok", "texts ok", "sms ok", "can text"]],
  ["leadSource", ["lead source", "how did you hear", "found us", "referral source", "source"]],
  ["prefer", ["preferred communication", "contact preference"]],
  ["archived", ["archived"]],
  ["tags", ["tags", "tag"]],
  ["notes", ["notes", "note", "comments", "instructions"]],
];

/** The phone columns in the order the number for texts is picked; home, work and other phones are never picked. */
const PHONE_ORDER: readonly (readonly [string, string, boolean])[] = [
  ["phoneText", "mobile", true],
  ["phoneMobile", "mobile", true],
  ["phoneMain", "main", true],
  ["phoneNumber", "other", true],
  ["phone", "other", true],
  ["cell", "mobile", true],
  ["phoneHome", "home", false],
  ["phoneWork", "work", false],
  ["phoneOther", "other", false],
];

/** Columns a field must never take, whatever else their name holds. */
function skip(field: string, h: string): boolean {
  return (
    (field === "address" && /e ?mail/.test(h)) ||
    (field === "name" && /(first|last|company|business|user|property|\bid\b)/.test(h)) ||
    (field === "company" && /^is\b/.test(h)) ||
    (PHONE_ORDER.some(([k]) => k === field) && h.includes("fax"))
  );
}

/** Which column each field reads from. */
export function importMap(headers: readonly string[]): Record<string, number> {
  const low = headers.map((h) =>
    h
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .trim(),
  );
  const map: Record<string, number> = {};
  const used = new Set<number>();
  for (const pass of ["exact", "loose"] as const)
    for (const [field, hints] of IMPORT_HINTS) {
      if (map[field] !== undefined) continue;
      for (const hint of hints) {
        const i = low.findIndex(
          (h, at) => !used.has(at) && (pass === "exact" ? h === hint : h.includes(hint)) && !skip(field, h),
        );
        if (i >= 0) {
          map[field] = i;
          used.add(i);
          break;
        }
      }
    }
  return map;
}

/** The phone numbers in one cell: several split by ; , / or a line break. Fewer than 7 digits is not a number. */
export function cellNumbers(v: string): string[] {
  const out: string[] = [];
  for (const part of v.split(/[;,\n\r]+/)) {
    const bits = part.split("/");
    for (const b of bits.length > 1 && bits.every((x) => x.replace(/\D/g, "").length >= 7) ? bits : [part])
      if (b.replace(/\D/g, "").length >= 7) out.push(b.replace(/[\u200e\u200f\u00a0]/g, " ").trim());
  }
  return out;
}

/** A day in a sheet: m/d/yyyy or yyyy-mm-dd. Null when it is not a real day, or before 2000 or after tomorrow. */
export function sheetDay(raw: string, tomorrow: string): string | null {
  const s = raw.trim();
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(\s|$)/.exec(s);
  let y: number;
  let mo: number;
  let d: number;
  if (m) [y, mo, d] = [Number(m[3]), Number(m[1]), Number(m[2])];
  else if ((m = /^(\d{4})-(\d{1,2})-(\d{1,2})(\s|T|$)/.exec(s))) [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  else return null;
  const t = new Date(Date.UTC(y, mo - 1, d));
  if (t.getUTCFullYear() !== y || t.getUTCMonth() !== mo - 1 || t.getUTCDate() !== d) return null;
  const day = t.toISOString().slice(0, 10);
  return day < "2000-01-01" || day > tomorrow ? null : day;
}

const STATES: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  "district of columbia": "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
};

/** "Louisiana" or "LA" to LA; anything else to "". */
export function stateCode(v: string): string {
  const two = v.replace(/[^A-Za-z]/g, "").toUpperCase();
  if (two.length === 2 && Object.values(STATES).includes(two)) return two;
  return (
    STATES[
      v
        .toLowerCase()
        .replace(/[^a-z]+/g, " ")
        .trim()
    ] ?? ""
  );
}

/** "123 Main St Apt 2, New Orleans, LA" from the parts; a city or state already on street 1 is written once. */
export function importAddress(street1: string, street2: string, city: string, state: string): string {
  const st = stateCode(state);
  let s = street1.trim().replace(/[\s,]*\b\d{5}(-\d{4})?\s*$/, "");
  if (st) s = s.replace(new RegExp(`[\\s,]*\\b${st}\\.?\\s*$`), "");
  if (city) {
    const m = new RegExp(`(^|[\\s,])${city.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`, "i").exec(s);
    if (m && /[a-z0-9]/i.test(s.slice(0, m.index))) s = s.slice(0, m.index);
  }
  s = s.replace(/[\s,]+$/, "");
  const street = [s, street2.trim()].filter(Boolean).join(" ");
  return [street, city.trim(), st].filter(Boolean).join(", ");
}

const yes = (v: string) => /^(yes|y|true|1|x|archived)$/i.test(v.trim());

export interface ImportedClient {
  /** The other system's id, kept as legacy id ("jobber:J-123"), so importing the same file again updates the client. */
  importId: string | null;
  name: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  /** Every other number and email, with what kind it is. */
  contacts: { kind: "phone" | "email"; value: string; label: string }[];
  address: string | null;
  zip: string | null;
  tags: string[];
  notes: string | null;
  leadSource: string | null;
  prefers: "text" | "email";
  active: boolean;
  /** The day they became a client in the other system. */
  since: string | null;
  servicesDone: { name: string; first: string | null; last: string | null; times: number | null }[];
  /** Paid before the CRM, kept on the client and counted in what they have spent (never a payment in Books). */
  beforeCrm: { cents: number; lastJob: string | null; jobNumbers: string | null } | null;
}

/** One row as a client, plus the problems that stop it. tomorrow keeps a typo'd future date out. */
export function importRow(
  head: Record<string, number>,
  row: readonly string[],
  tomorrow: string,
): { client: ImportedClient | null; problems: string[] } {
  const cell = (k: string) => (head[k] === undefined ? "" : (row[head[k]] ?? "").trim());
  const problems: string[] = [];
  const first = cell("first") || null;
  const last = cell("last") || null;
  const company = cell("company") || null;
  const name = cell("name") || [first, last].filter(Boolean).join(" ") || (company ?? "");
  if (!name) return { client: null, problems: ["needs a name"] };

  const contacts: ImportedClient["contacts"] = [];
  let phone: string | null = null;
  for (const [field, label, textable] of PHONE_ORDER)
    for (const raw of cellNumbers(cell(field))) {
      const n = readPhone(raw);
      if (!n) {
        problems.push(`phone "${raw}" does not look right`);
        continue;
      }
      if (n === phone || contacts.some((c) => c.value === n)) continue;
      if (!phone && textable) phone = n;
      else contacts.push({ kind: "phone", value: n, label });
    }
  const mails = [...cell("email").split(/[\s,;]+/), ...cell("email2").split(/[\s,;]+/)]
    .map((e) => e.trim().toLowerCase())
    .filter((e) => /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(e));
  const email = mails[0] ?? null;
  for (const e of [...new Set(mails.slice(1))]) if (e !== email) contacts.push({ kind: "email", value: e, label: "other" });

  // A client whose main number does not take texts: the next mobile takes texts, else invoices go by email.
  let prefers: "text" | "email" = /e-?mail/i.test(cell("prefer")) ? "email" : "text";
  const notes = [cell("notes")];
  if (/call/i.test(cell("prefer"))) notes.push("Prefers a call");
  if (/^(no|n|false|0|off)$/i.test(cell("textOk")) && phone) {
    const other = contacts.findIndex((c) => c.kind === "phone");
    if (other >= 0) {
      const old = phone;
      phone = contacts[other]!.value;
      contacts.splice(other, 1, { kind: "phone", value: old, label: "main" });
      notes.push(`Texts are off for ${old}, so texts go to ${phone}`);
    } else if (email) {
      prefers = "email";
      notes.push("Texts are off for this client, so invoices go by email");
    }
  }

  const zip = /^(\d{5})(-?\d{4})?$/.exec(cell("zip"))?.[1] ?? null;
  const address = cell("address") ? importAddress(cell("address"), cell("address2"), cell("city"), cell("state")) : null;
  if (!address && !phone && !email) problems.push("needs an address, a phone number or an email");

  const svFirst = sheetDay(cell("svcFirst"), tomorrow);
  const svLast = sheetDay(cell("svcLast"), tomorrow);
  const [lo, hi] = svFirst && svLast && svLast < svFirst ? [svLast, svFirst] : [svFirst, svLast];
  const kinds = [
    ...new Set(
      cell("servicesDone")
        .split(/[;\r\n]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  ];
  const jobs = Number(cell("jobsCount").replace(/\D/g, "")) || 0;
  const servicesDone = kinds.map((k) => ({
    name: k,
    first: lo,
    last: hi ?? lo,
    times: jobs > 0 && (jobs === 1 || kinds.length === 1) ? jobs : null,
  }));
  const spend = Number(cell("spend").replace(/[$,\s]/g, ""));
  const beforeCrm =
    Number.isFinite(spend) && spend > 0
      ? { cents: Math.round(spend * 100), lastJob: hi ?? lo, jobNumbers: cell("jobNumbers").replace(/\s+/g, " ") || null }
      : null;

  const jid = cell("jobberId");
  const iid = cell("importId");
  return {
    client: {
      importId: jid ? `jobber:${jid}` : iid ? `import:${iid}` : null,
      name,
      firstName: first,
      lastName: last,
      companyName: company,
      phone,
      email,
      contacts,
      address,
      zip,
      tags: cell("tags")
        .split(/[,;]+/)
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 20),
      notes: notes.filter(Boolean).join("\n") || null,
      leadSource: cell("leadSource").slice(0, 40) || null,
      prefers,
      active: !yes(cell("archived")),
      since: sheetDay(cell("createdAt"), tomorrow) ?? lo,
      servicesDone,
      beforeCrm,
    },
    problems,
  };
}

/** Every row of a sheet read: the header map, then each row's client and problems (the first row is the header). */
export function readClientSheet(
  text: string,
  tomorrow: string,
): { columns: string[]; rows: { line: number; client: ImportedClient | null; problems: string[] }[] } {
  const all = csvRows(text);
  const [header = [], ...rest] = all;
  const head = importMap(header);
  return {
    columns: Object.keys(head),
    rows: rest.slice(0, 5000).map((r, i) => ({ line: i + 2, ...importRow(head, r, tomorrow) })),
  };
}
